import { useCallback, useMemo } from "react";
import { ContractingCompaniesRepository } from "@/api/contracting-companies/repository";
import { ContractingCompaniesService } from "@/api/contracting-companies/service";
import { LendersRepository } from "@/api/lenders/repository";
import { LendersService } from "@/api/lenders/service";
import type { IGetLendersRequest } from "@/api/lenders/types";
import type { ISelectOption } from "@/ui-kit";
import { Avatar } from "@/ui-kit";
import type { IFetchOptions } from "../useSearchOptions";
import { useSearchOptions } from "../useSearchOptions";

export type ICompanyType = "contractor" | "lender";

type ICompany = {
  id: number;
  name: string;
  companyLogo: string;
};

const PAGE_SIZE = "20";

// useSearchOptions aborts its own requests, so repositories run without `abort`.
const createRepository = (company: ICompanyType) => {
  switch (company) {
    case "contractor":
      return new ContractingCompaniesRepository(
        new ContractingCompaniesService(),
      );
    case "lender":
      return new LendersRepository(new LendersService());
  }
};

const toOption = (company: ICompany): ISelectOption => {
  return {
    id: company.id.toString(),
    text: company.name,
    icon: <Avatar size="xs" src={company.companyLogo} showDetails={false} />,
  };
};

/** Paginated, searchable list of companies of one type. */
export const useCompanyOptions = (company: ICompanyType) => {
  const repository = useMemo(() => createRepository(company), [company]);

  const fetchOptions = useCallback<IFetchOptions>(
    (query, page, signal) => {
      // Both company endpoints take the same list params.
      const params: IGetLendersRequest = {
        searchQuery: query,
        page: `${page}`,
        size: PAGE_SIZE,
        sortField: "name",
        sortOrder: "asc",
      };

      return repository.getAll(params, { signal }).then((response) => ({
        options: response.content.map(toOption),
        hasMore: response.page < response.totalPages,
      }));
    },
    [repository],
  );

  return useSearchOptions(fetchOptions);
};
